import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { Tooltip } from 'components/tooltip';
import { Button } from 'components/buttons';
import { Money } from 'components/formatting/Money';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { Modal } from 'components/notifications';
import { DataDisplayTable } from 'components/data-display';
import PropTypes from 'prop-types';
import { PageRestricted } from 'pages/errors/page-restricted';
import { CollisionsService } from 'services';


export function CollisionListing({
    profileType = "employee",
    entityId
}) {
    const pageNo = 1;
    const pageSize = 100;
 
    const [loadingData, setLoadingData] = useState(true);
    const [rawTableData, setRawTableData] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [collisionInfo, setCollisionInfo] = useState([]);
    const [tableHeaders, setTableHeaders] = useState(getTableHeaders());
    const [totalRecords, setTotalRecords] = useState(0);

    const requiredAction = "Collisions:View";
    const canAccessPage = UserHasAccess(requiredAction);

    useEffect(() => {
        fetchTableData();
    }, []);

    function fetchTableData() {
        if (!canAccessPage) {
            setLoadingData(false);
            return;
        }

        setLoadingData(true);

        var filters = [{ name: `${profileType === "employee" ? "EmployeeId" : "AssetId"}`, value: entityId }];

        CollisionsService.getAllCollisions(pageNo, pageSize, null, "Incident Date", "D", filters, [])
            .then(function (r) {
                setRawTableData(r.data);
                setTotalRecords(r.totalCount);
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    useEffect(() => {
        const headers = getTableHeaders();
        setTableHeaders(headers);
    }, [profileType]);

    function getTableHeaders() {
        const headers = ["Incident Date"];

        headers.push(`${profileType === "employee" ? "Registration" : "Driver"}`);
        headers.push("Fault Status");
        headers.push("Injury Level");
        headers.push("Damage Level");
        headers.push("Third Party Involved");
        headers.push("Details");

        return headers;
    }

    function displayFriendlyBoolean(value) {
        if (value == null) {
            return "Unknown"
        }

        return value ? "Yes" : "No";
    }

    function displayFriendlyFault(value) {
        if (value == null) {
            return "Unknown"
        }

        return value ? "At fault" : "Not at fault";
    }

    function displayFriendlyValue(value) {
        return value == null ? "Unknown" : value;
    }
    
    function displayFriendlyLink(value, url, id) {
        return value == null ? <span>Unknown</span> : <a href={`${url}${id}`}>{value}</a>;
    }

    function viewCollisionInfo(collision) {
        var info = [
            {
                header: "Incident Date",
                value: <DateToLocal format="DD/MM/YYYY">{collision.incidentDate}</DateToLocal>,
                key: 'incident-date'
            },
            {
                header: "Employee",
                value: displayFriendlyLink(collision.employeeName, `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=`, collision.employeeId),
                key: 'employee'
            },
            {
                header: "Employee Location",
                value: displayFriendlyValue(collision.employeeLocation),
                key: 'employee-location'
            },
            {
                header: "Asset Registration",
                value: displayFriendlyLink(collision.assetRegistration, `${window.env.VISION_URL}/#/Fleet/RedirectToProfile?tab=detailsTab&assetId=`, collision.assetId),
                key: 'asset'
            },
            {
                header: "Asset Location",
                value: displayFriendlyValue(collision.assetLocation),
                key: 'asset-location'
            },
            {
                header: "Fault Status",
                value: displayFriendlyFault(collision.driverAtFault),
                key: 'fault'
            },
            {
                header: "Injury Level",
                value: displayFriendlyValue(collision.collisionInjuryLevel),
                key: 'injury'
            },
            {
                header: "Damage Level",
                value: displayFriendlyValue(collision.collisionDamageLevel),
                key: 'damage'
            },
            {
                header: "Third Party Involved",
                value: displayFriendlyBoolean(collision.thirdPartyInvolvement),
                key: 'third-party'
            },
            {
                header: "Area of Damage",
                value: displayFriendlyValue(collision.areaOfDamage),
                key: 'damage-area'
            },
            {
                header: "Repair Cost",
                value: <Money>{collision.repairCost}</Money>,
                key: 'repair-cost'
            },
            {
                header: "Total Cost",
                value: <Money>{collision.totalCost}</Money>,
                key: 'total-cost'
            },
        ];

        setCollisionInfo(info);
    }

    useEffect(() => {
        let data = [];

        if (rawTableData != null) {
            data = rawTableData.map((d) => {

                return [
                    <DateToLocal key={`incident-date-${d.collisionId}`} format="DD/MM/YYYY" >{d.incidentDate}</DateToLocal>,
                    <ShowHide
                        evaluator={profileType === "employee"}
                        key={`employee-name-${d.collisionId}`}
                        show={(
                            <Link to={`/asset/${d.assetId}/collisions`}>{d.assetRegistration}</Link>
                            
                        )}
                        hide={(
                            <Link to={`/employee/${d.employeeId}/collisions`}>{d.employeeName}</Link>
                        )}
                    />,
                    displayFriendlyFault(d.driverAtFault),
                    displayFriendlyValue(d.collisionInjuryLevel),
                    displayFriendlyValue(d.collisionDamageLevel),
                    displayFriendlyBoolean(d.thirdPartyInvolvement),
                    <div className="button-row-small" key={`incident-actions-${d.collisionId}`}>
                        <Tooltip content="View more details">
                            <Button 
                                onClick={() => viewCollisionInfo(d)} 
                                icon="document-open" 
                                iconOnly={true} 
                                text="View details" 
                                large={false} 
                                minimal={true}
                            ></Button>
                        </Tooltip>
                    </div>];

            })
        }

        setTableRows(data);
    }, [rawTableData]);

    function onInfoDismiss() {
        setCollisionInfo([]);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">

                    <h1>Collisions</h1>

                    <div className="intro-text">
                        <p>
                            {`In this section you can view the collisions for the ${profileType}. Adding and editing collisions can be carried out on the Collisions page `}
                            <Link to={`/collisions`}>here</Link>.
                        </p>
                    </div>

                    <ListingTable
                        id="listing-table-collisions"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loadingData}
                        noDataMessage={"You have no collisions recorded."}
                        pageable={false}
                    />

                    <Modal
                        isOpen={collisionInfo.length > 0}
                        title={"Collision Details"}
                        onClose={onInfoDismiss}
                    >
                        <DataDisplayTable
                            data={collisionInfo}
                            displayInRows={true}
                            loading={loadingData}
                        />
                    </Modal>
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    )
}

CollisionListing.propTypes = {
    profileType: PropTypes.string,
    entityId: PropTypes.string
};